import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { FoodSelect, apiErr, fmtDate } from "@/merenda/common";

const LINK = { RECEITA: ["Receita salva", "bg-green-100 text-green-700"], IA: ["IA", "bg-blue-100 text-blue-700"], NENHUM: ["Sem vínculo", "bg-red-100 text-red-700"] };
const UNITS = ["kg", "L", "uni", "dz", "pct", "cx", "g"];

const NewFoodDialog = ({ name, scopeParams, onClose, onCreated }) => {
  const [n, setN] = useState(name);
  const [unit, setUnit] = useState("kg");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post("/merenda/foods", { name: n, unit, decimals: unit === "uni" ? 0 : 3 }, { params: scopeParams }); toast.success(`${data.name} cadastrado`); onCreated(data, name); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="new-food-dialog">
        <DialogHeader><DialogTitle>Cadastrar alimento</DialogTitle></DialogHeader>
        <Input value={n} onChange={(e) => setN(e.target.value)} data-testid="new-food-name" />
        <div className="flex flex-wrap gap-1">{UNITS.map((u) => <Button key={u} size="sm" variant={u === unit ? "default" : "outline"} onClick={() => setUnit(u)} data-testid={`new-food-unit-${u}`}>{u}</Button>)}</div>
        <DialogFooter><Button disabled={busy || !n.trim()} onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="new-food-save">Cadastrar e associar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MenuImportRows = ({ rows, setRows, foods, mealLb, scopeParams, onFoodsChanged }) => {
  const [creating, setCreating] = useState(null);
  const fm = Object.fromEntries(foods.map((f) => [f.id, f]));
  const upd = (i, patch) => setRows(rows.map((y, j) => (j === i ? { ...y, ...patch } : y)));
  const created = async (food, orig) => {
    setCreating(null);
    await onFoodsChanged?.();
    const k = orig.toLowerCase();
    setRows(rows.map((r) => ((r.missing || []).some((m) => m.toLowerCase() === k)
      ? { ...r, food_ids: [...new Set([...(r.food_ids || []), food.id])], missing: r.missing.filter((m) => m.toLowerCase() !== k) } : r)));
  };
  const noLink = rows.filter((r) => !(r.food_ids || []).length).length;
  return (
    <>
      <p className="text-xs text-slate-600" data-testid="import-menu-stats">{rows.length} refeições · {rows.length - noLink} com alimentos associados · <span className={noLink ? "font-bold text-red-600" : ""}>{noLink} sem alimentos</span>. Ingredientes em laranja não estão no catálogo: clique para cadastrar.</p>
      <Table>
        <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Refeição</TableHead><TableHead>Preparação</TableHead><TableHead className="min-w-[280px]">Alimentos</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((x, i) => (
          <TableRow key={i} data-testid={`import-menu-row-${i}`}>
            <TableCell className="whitespace-nowrap">{fmtDate(x.date)}</TableCell><TableCell>{mealLb[x.meal]}{x.comensais != null && <div className="text-xs text-slate-400">{x.comensais} com.</div>}</TableCell>
            <TableCell className="min-w-[200px]"><Input value={x.preparation || ""} onChange={(e) => upd(i, { preparation: e.target.value })} data-testid={`import-menu-prep-${i}`} />
              {x.link && <Badge className={`mt-1 ${LINK[x.link][1]}`}>{LINK[x.link][0]}</Badge>}</TableCell>
            <TableCell>
              <div className="mb-1 flex flex-wrap gap-1">
                {(x.food_ids || []).map((id) => <span key={id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs">{fm[id]?.name}<button onClick={() => upd(i, { food_ids: x.food_ids.filter((y) => y !== id) })} data-testid={`import-menu-rm-${i}-${id}`}><X className="h-3 w-3" /></button></span>)}
                {(x.missing || []).map((m) => <button key={m} onClick={() => setCreating(m)} className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-800 hover:bg-orange-200" data-testid={`import-menu-missing-${i}-${m}`}><Plus className="h-3 w-3" />{m}</button>)}
              </div>
              <FoodSelect foods={foods} value="" onChange={(v) => v && !(x.food_ids || []).includes(v) && upd(i, { food_ids: [...(x.food_ids || []), v] })} testid={`import-menu-add-${i}`} />
            </TableCell>
          </TableRow>))}</TableBody>
      </Table>
      {creating && <NewFoodDialog name={creating} scopeParams={scopeParams} onClose={() => setCreating(null)} onCreated={created} />}
    </>
  );
};
